import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Actions as Snack } from '../../../store/ducks/snacks';
import ee from '../../../services/earth-engine';
import {
  Box,
  Button,
  Slider,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  TextField,
  MenuItem,
} from '@material-ui/core';
import { Visibility, VisibilityOff, Delete, Edit } from '@material-ui/icons';
import GoogleMap from '../map/GoogleMap';
import StepperButtons from '../acquisition/StepperButtons';
import * as MapGoogle from '../../../common/map';
import TourGuider from '../tour/TourGuider';
import { useLocalStorage } from '../../../common/utils';
import HelpButton from '../core/HelpButton';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import {
  acquireFromDate as acquireFromDateSentinel,
  maskS2Clouds,
  maskS2ImageMask,
} from '../../../algorithms/satellite/sentinel';
import {
  acquireFromDate as acquireFromDateLandsat,
  fillGaps,
  getBandMappingForSatellite,
} from '../../../algorithms/satellite/landsat';
import Dygraph from 'dygraphs';
//export shapefile dependencies
import Export from '../../../services/export';
import { INTERNALS } from '../../../common/metadata';
import { omit } from 'lodash';
import JSZip from 'jszip';
import { getSelectedSatellite } from '../../../algorithms/utils';
import { aoiGeometry } from '../../../common/aoi';

/**
 * @function isSentinelMission
 * @description Checks whether a mission is a Sentinel-2 (COPERNICUS/S2) mission.
 * @param {Object} mission - Mission configuration object.
 * **Called by:
 * @see {getRgbBands}
 * @see {getMissionBands}
 * @see {getMissionVizParams}
 * @see {getMissionResolution}
 * @memberof module:INTERTIDAL
 * @returns {boolean} True if the mission is Sentinel-2.
 */
const isSentinelMission = (mission) => {
  if (!mission?.name) return false;
  return mission.name.indexOf('COPERNICUS') !== -1 || mission.name.indexOf('S2') !== -1;
};

/**
 * @function isLandsatMission
 * @description Checks whether a mission is a Landsat mission.
 * @param {Object} mission - Mission configuration object.
 * **Called by:
 * @see {IntertidalZoneDefiner} - via initializeMapAndGeometry and calculateIntertidalZones.
 * @memberof module:INTERTIDAL
 * @returns {boolean} True if the mission is Landsat.
 */
const isLandsatMission = (mission) => {
  if (!mission?.name) return false;
  return mission.name.indexOf('LANDSAT') !== -1;
};

/**
 * @function getRgbBands
 * @description Resolves the RGB band names to use for true-color visualization, based on the
 * mission's satellite and processing level.
 * @param {Object} mission - Mission configuration object.
 * @see {@link isSentinelMission}
 * **Called by:
 * @see {IntertidalZoneDefiner} - via drawBaseLayers and calculateIntertidalZones.
 * @memberof module:INTERTIDAL
 * @returns {Array<String>} [red, green, blue] band names.
 */
const getRgbBands = (mission) => {
  if (isSentinelMission(mission)) return ['B4', 'B3', 'B2'];
  if (mission?.name) {
    if (mission.name.indexOf('LT05') !== -1 || mission.name.indexOf('LE07') !== -1) {
      return ['SR_B3', 'SR_B2', 'SR_B1'];
    }
    if (mission.name.indexOf('LC08') !== -1 || mission.name.indexOf('LC09') !== -1) {
      return ['SR_B4', 'SR_B3', 'SR_B2'];
    }
    if (mission.name.indexOf('T1_TOA') !== -1 || mission.name.indexOf('T2_TOA') !== -1) {
      return ['B4', 'B3', 'B2'];
    }
  }
  if (mission?.bands?.red) return [mission.bands.red, mission.bands.green, mission.bands.blue];
  return ['SR_B4', 'SR_B3', 'SR_B2'];
};

/**
 * @function getMissionBands
 * @description Resolves the named band mapping (red/green/blue/nir/swir/swir2/qa) for a mission,
 * based on its satellite and processing level, falling back to Landsat 8/9 if unrecognized.
 * @param {Object} mission - Mission configuration object.
 * @see {@link isSentinelMission}
 * **Called by:
 * @see {IntertidalZoneDefiner} - via calculateIntertidalZones.
 * @memberof module:INTERTIDAL
 * @returns {Object} Named band mapping for the mission.
 */
const getMissionBands = (mission) => {
  if (mission?.bands) {
    return mission.bands;
  }

  if (mission?.name) {
    if (mission.name.indexOf('LT05') !== -1 || mission.name.indexOf('LE07') !== -1) {
      return {
        red: 'SR_B3',
        green: 'SR_B2',
        blue: 'SR_B1',
        nir: 'SR_B4',
        swir: 'SR_B5',
        swir2: 'SR_B7',
        qa: 'QA_PIXEL',
      };
    }
    if (mission.name.indexOf('LC08') !== -1 || mission.name.indexOf('LC09') !== -1) {
      return {
        red: 'SR_B4',
        green: 'SR_B3',
        blue: 'SR_B2',
        nir: 'SR_B5',
        swir: 'SR_B6',
        swir2: 'SR_B7',
        qa: 'QA_PIXEL',
      };
    }
    if (mission.name.indexOf('COPERNICUS') !== -1 || mission.name.indexOf('S2') !== -1) {
      return {
        blue: 'B2',
        green: 'B3',
        red: 'B4',
        nir: 'B8',
        swir: 'B11',
        qa: 'QA60',
      };
    }
  }
  return {
    red: 'SR_B4',
    green: 'SR_B3',
    blue: 'SR_B2',
    nir: 'SR_B5',
    swir: 'SR_B6',
    swir2: 'SR_B7',
    qa: 'QA_PIXEL',
  };
};

/**
 * @function getMissionVizParams
 * @description Resolves the min/max visualization parameters for a mission's true-color preview.
 * @param {Object} mission - Mission configuration object.
 * @see {@link isSentinelMission}
 * **Called by:
 * @see {IntertidalZoneDefiner} - via drawBaseLayers and calculateIntertidalZones.
 * @memberof module:INTERTIDAL
 * @returns {Object} {min, max} visualization parameters.
 */
const getMissionVizParams = (mission) => {
  if (mission?.vizParams?.max !== undefined) {
    return mission.vizParams;
  }
  if (isSentinelMission(mission)) {
    return { max: 3000, min: 100 };
  }
  return { min: 0.0, max: 0.3 };
};

/**
 * @function getMissionResolution
 * @description Resolves a mission's native optical resolution, in meters.
 * @param {Object} mission - Mission configuration object.
 * @see {@link isSentinelMission}
 * **Called by:
 * @see {IntertidalZoneDefiner} - via calculateIntertidalZones.
 * @memberof module:INTERTIDAL
 * @returns {Number} Resolution in meters.
 */
const getMissionResolution = (mission) => {
  if (mission?.opticalResolution) return mission.opticalResolution;
  if (isSentinelMission(mission)) return 10;
  return 30;
};

/**
 * @description Static cache holding intermediate intertidal analysis state shared across steps:
 * the map overlay cache, download URLs, the vectorized zone to export, the images cached for the
 * next step, and the cached zone-index images/threshold/layer URLs. The `awei*` field names
 * predate the index picker (step 4 used to hardcode AWEI) — they now hold whichever index the
 * user picked as the zone-identification index, not necessarily AWEI; kept as-is to avoid
 * renaming across `VectorEditor.jsx`/`result.jsx`/`kriging.js` for no functional gain.
 * `waterlineIndex` is the separate index picked for step 5's coastline/waterline detection
 * (`result.jsx`'s `calculateWaterlineForImage`), independent of the zone index above.
 * @see {@link createAweiMask}
 * @memberof module:INTERTIDAL
 */
export class OverlayHelper {
  static overlayCache = [];
  static imgDownloadURL = [];
  static urlImagMapLayerDownload = '';
  static vectorsToExport = null;
  static imgsToStep5 = [];
  static aweiLayerURLs = [];
  static aweiThreshold = 0;
  static aweiImages = [];
  static waterlineIndex = 'ndwi';
  static zoneIndex = 'awei';
}

// The 4 spectral index bands `indexFunction` (inside calculateIntertidalZones) always computes
// and adds to every image — shared by both index selectors below, and used elsewhere (e.g.
// the matching i18n key under `forms.intertidal.*`.
export const INDEX_OPTIONS = [
  { value: 'awei', labelKey: 'indexAwei' },
  { value: 'ndwi', labelKey: 'indexNdwi' },
  { value: 'mndwi', labelKey: 'indexMndwi' },
  { value: 'mndwi_sharp', labelKey: 'indexMndwiSharp' },
];

const SELECTABLE_INDEX_OPTIONS = INDEX_OPTIONS.filter((option) => option.value !== 'mndwi_sharp');

/**
 * @function IntertidalZoneDefiner
 * @description Third step of the intertidal acquisition wizard. Loads the acquired images,
 * computes the AWEI-based intertidal zone for each threshold the user tries, lets the user run
 * multiple named analyses (each with its own threshold/layers), and prepares the vectorized zone
 * and cached images consumed by the following steps (VectorEditor, WaterlineIdentification).
 * @param {Object} props - Component properties.
 * @param {Function} props.navigate - Step navigation handler, forwarded to StepperButtons.
 * @see {@link initializeMapAndGeometry} - one-time map/geometry/image setup.
 * @see {@link calculateIntertidalZones} - core AWEI zone computation.
 * @see {@link handleChoose} - validates and submits the step.
 * @see {@link OverlayHelper}
 * **Called by:
 * @see {IntertidalPage} - as the third acquisition step.
 * @memberof module:INTERTIDAL
 * @returns {JSX.Element} The intertidal zone definition UI.
 */
const IntertidalZoneDefiner = ({ navigate, ...extra }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const [overlay, setOverlay] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  const [manualThreshold, setManualThreshold] = useState(-1);
  const [zoneIndex, setZoneIndex] = useState(OverlayHelper.zoneIndex || 'awei');
  const [editingAnalysisId, setEditingAnalysisId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  /**
   * What this step is waiting for. One flag covers the zone calculation and the image download, so
   * without it the indicator says "carregando" for a wait of minutes and for a wait of seconds
   * alike.
   */
  const [loadingLabel, setLoadingLabel] = useState('');
  const [waterlineIndex, setWaterlineIndex] = useState(OverlayHelper.waterlineIndex || 'ndwi');
  const [layers, setLayers] = useState({});
  const [layerVisibility, setLayerVisibility] = useState({});
  const layersRecreated = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const dates = useSelector((state) => state.acquisition.availableDates);

  const [dataBar, setDataBar] = useState([]);
  const [thresholdJSNum, setThresholdJSNum] = useState(0);

  const [analyses, setAnalyses] = useState([]);
  const analysisCounterRef = useRef(1);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);

  const initializationDone = useRef(false);
  const geometryRef = useRef(null);
  const imagesRef = useRef([]);
  const boundsRef = useRef(null);
  const aoiPolygonRef = useRef(null);
  const mapRef = useRef(window.initMap);
  const zoomSaveIntervalRef = useRef(null);
  const missionRef = useRef(null);
  const markedPointsRef = useRef({});
  const centerRef = useRef(null);

  /**
   * @function initializeMapAndGeometry
   * @description Initializes the map, AOI geometry, per-date images, and AOI polygon. Runs only
   * once, on component mount.
   * 1. Resolves the selected satellite/mission and builds an Earth Engine mosaic image for each
   *    available date.
   * 2. Draws the AOI polygon on the map and computes its bounds/center.
   * 3. Starts a 5-second interval that persists the map's zoom level to session storage.
   * @param {Array} availableDates - Array of available dates from Redux.
   * @see {@link isSentinelMission}
   * @see {@link isLandsatMission}
   * **Called by:
   * @see {IntertidalZoneDefiner} - once, when the available dates are loaded.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const initializeMapAndGeometry = (availableDates) => {
    console.log('=== Starting component initialization ===');
    console.log('Dates received:', availableDates?.length || 0);

    if (!availableDates || availableDates.length === 0) {
      console.error('ERROR: No dates available to process!');
      return;
    }
    const satellite = getSelectedSatellite(true);
    const type = window.sessionStorage.getItem('typeSatellite');
    const missions = type === 'TOA' ? satellite.missions : satellite.surfaceReflectance;
    const coordinatesArray = JSON.parse(window.localStorage.getItem('AOIIntertidal'));
    let mission = JSON.parse(window.sessionStorage.getItem('selectedMission'));

    if (!mission && availableDates && availableDates.length > 0) {
      const selectedSatelliteIndex = Number.parseInt(window.sessionStorage.getItem('selectedSatelliteIndex') || '0');

      const firstImageName = availableDates[0].name;
      mission = missions.find((m) => m.name === firstImageName);

      if (!mission && missions.length > 0) {
        mission = missions[0];
      }
      if (mission) {
        window.sessionStorage.setItem('selectedMission', JSON.stringify(mission));
      }

      console.log('Mission obtained from satellite:', mission?.name);
    }
    missionRef.current = mission;
    const geometry = aoiGeometry(coordinatesArray);
    geometryRef.current = geometry;
    const images = [];
    availableDates.forEach((el) => {
      let date = el.date;
      let imageMission = missions.find((m) => m.name === el.name) || mission;

      if (!imageMission) {
        console.warn('No mission found for image:', el.name);
        return;
      }

      let mosaicImage;
      if (isSentinelMission(imageMission)) {
        mosaicImage = acquireFromDateSentinel(date, imageMission, geometry, true, true);
      } else if (isLandsatMission(imageMission)) {
        mosaicImage = acquireFromDateLandsat(date, imageMission, geometry, true);
      }

      mosaicImage
        ? images.push(mosaicImage)
        : console.warn('Failed to acquire image for date:', date, 'mission:', imageMission?.name);
    });
    imagesRef.current = images;
    console.log('2. Images created:', images.length);

    const formatedCoordinates = coordinatesArray.map((coord) => {
      return { lat: coord[1], lng: coord[0] };
    });

    const AOI = new window.google.maps.Polygon({
      path: formatedCoordinates,
      geodesic: true,
      fillColor: 'transparent',
      strokeColor: '#00A391',
      strokeOpacity: 1,
      strokeWeight: 1,
      clicable: false,
    });
    AOI.setMap(mapRef.current);
    aoiPolygonRef.current = AOI;

    const bounds = new window.google.maps.LatLngBounds();
    AOI.getPath()
      .getArray()
      .forEach((coord) => {
        let latLng = new window.google.maps.LatLng(coord.lat(), coord.lng());
        bounds.extend(latLng);
      });

    boundsRef.current = bounds;
    console.log('3. Bounds calculated');

    const center = bounds.getCenter();
    centerRef.current = center;
    mapRef.current.fitBounds(bounds);

    /**
     * @function saveZoomChange
     * @description Persists the map's current zoom level to session storage.
     * **Called by:
     * @see {initializeMapAndGeometry} - polled every 5 seconds via setInterval.
     * @memberof module:INTERTIDAL
     * @returns {void}
     */
    const saveZoomChange = () => {
      let zoom = MapGoogle.getZoomLevel();
      if (typeof zoom !== 'undefined' && zoom !== 'undefined') {
        window.sessionStorage.setItem('zoom_level', zoom);
      }
    };

    window.sessionStorage.removeItem('zoom_level');

    if (zoomSaveIntervalRef.current) clearInterval(zoomSaveIntervalRef.current);
    zoomSaveIntervalRef.current = setInterval(saveZoomChange, 5000);

    console.log('=== Initialization completed ===');
  };

  useEffect(() => {
    const checkInterval = setInterval(() => {
      if (window.initMap && window.initMap.getBounds) {
        console.log('Map is ready! (detected via interval)');
        mapRef.current = window.initMap;
        clearInterval(checkInterval);
        setMapReady(true);
      }
    }, 100);

    if (window.initMap && !mapReady) {
      const checkMapReady = () => {
        console.log('Map is ready! Tiles loaded (via listener).');
        clearInterval(checkInterval);
        setMapReady(true);
      };

      try {
        window.google.maps.event.addListenerOnce(window.initMap, 'tilesloaded', checkMapReady);
      } catch (e) {
        console.warn('Could not add listener to map:', e);
      }
    }

    return () => {
      clearInterval(checkInterval);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (zoomSaveIntervalRef.current) {
        clearInterval(zoomSaveIntervalRef.current);
      }
    };
  }, []);

  /**
   * @function drawBaseLayers
   * @description Draws each acquired date's true-color RGB thumbnail as a GroundOverlay on the
   * map. Not currently called anywhere in the codebase.
   * @see {@link getMissionBands}
   * @see {@link getMissionVizParams}
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const drawBaseLayers = () => {
    console.log('=== Drawing base layers ===');

    const geometry = geometryRef.current;
    const mission = missionRef.current;
    const bounds = boundsRef.current;
    const images = imagesRef.current;

    const missionBands = getMissionBands(mission);
    console.log('Mission bands for visualization:', missionBands);
    const missionVizParams = getMissionVizParams(mission);
    const thumbnailParams = {
      region: geometry,
      bands: [missionBands.red, missionBands.green, missionBands.blue],
      dimensions: 512,
      max: missionVizParams.max,
      min: missionVizParams.min,
      format: 'jpg',
    };

    const values = images.map((value) => {
      let thumbnailURL = value.getThumbURL(thumbnailParams);
      return {
        date: 'intertidal',
        falseColorUrl: thumbnailURL,
        imageObject: value,
        missionName: 'COPERNICUS/S2_SR_HARMONIZED',
        name: 'intertidal',
        url: thumbnailURL,
      };
    });

    const newLayers = {};
    values.forEach((image, i) => {
      var overlay = new window.google.maps.GroundOverlay(image.url, bounds);
      overlay.setMap(mapRef.current);
      OverlayHelper.overlayCache.push(overlay);

      newLayers[image.date] = { ...image, overlay };
      markedPointsRef.current = { [image.date]: [] };
    });

    setLayers(newLayers);
    window.layers = newLayers;

    console.log('=== Base layers drawn ===');
  };

  /**
   * @function recreateLayersFromCache
   * @description Redraws the cached AWEI GroundOverlays (persisted to session storage as URLs) on
   * the map, restoring their visibility state. Not currently called anywhere in the codebase.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const recreateLayersFromCache = () => {
    console.log('=== Recreating layers from cache ===');

    const savedLayerURLs = window.sessionStorage.getItem('intertidalLayerURLs');
    if (!savedLayerURLs || layersRecreated.current) {
      console.log('No cache data or already recreated');
      return;
    }

    const layerData = JSON.parse(savedLayerURLs);
    const bounds = boundsRef.current;

    window.layersCalculated = [];
    const initialVisibility = {};

    layerData.forEach((layerInfo) => {
      let overlayIntertidalZone = new window.google.maps.GroundOverlay(layerInfo.url, bounds);
      overlayIntertidalZone.setMap(mapRef.current);
      window.layersCalculated.push(overlayIntertidalZone);
      OverlayHelper.overlayCache.push(overlayIntertidalZone);

      initialVisibility[layerInfo.date] = true;
    });

    layersRecreated.current = true;
    setLayerVisibility(initialVisibility);

    console.log('=== AWEI layers recreated. Total:', window.layersCalculated.length, '===');
  };

  /**
   * @function calculateIntertidalZones
   * @description Computes the AWEI-based intertidal zone for the acquired images and creates one
   * AWEI GroundOverlay layer per date on the map, storing the result as a new named analysis.
   * 1. Builds a masked, reflectance-corrected image collection (satellite-aware: cloud/land
   *    masking, gap-filling for Landsat) and derives NDWI/MNDWI/AWEI bands for each image.
   * 2. Computes the standard deviation of AWEI across the collection and its histogram, then
   *    derives the intertidal threshold — either via Otsu's method or the given manual value.
   * 3. Thresholds the standard-deviation image into a binary zone mask, vectorizes it into
   *    polygons, and clips every image to the vectorized zone.
   * 4. For each date, generates the AWEI thumbnail/GroundOverlay, download URLs (clipped and
   *    unclipped), and caches everything needed by the following steps in `OverlayHelper`.
   * 5. Packages the result as a new named analysis and, if it's the first one, selects it and
   *    syncs `OverlayHelper` to it.
   * @param {number} [manualThresholdValue=-1] - Manual threshold (use -1 for automatic Otsu calculation).
   * @param {Array} [availableDates=null] - Available dates to use; falls back to the Redux `dates` selector.
   * @param {String} [editAnalysisId=null] - When given, replaces that existing analysis in place
   * (same id/name/position in the list) instead of appending a new one — used when the user
   * recalculates an analysis via its "Editar" button.
   * @see {@link getMissionBands}
   * @see {@link getMissionResolution}
   * @see {@link getRgbBands}
   * @see {@link isSentinelMission}
   * @see {@link isLandsatMission}
   * @see {@link OverlayHelper}
   * **Called by:
   * @see {handleCreateRasterWithSelectedIndices} - when the user presses "Criar" with the picked indices.
   * @see {redoThreshold} - when the user confirms a manual threshold.
   * @memberof module:INTERTIDAL
   * @returns {Object} {dataBar, threshold, analysisId} - Histogram data, threshold, and the new analysis's ID.
   */
  const calculateIntertidalZones = (manualThresholdValue = -1, availableDates = null, editAnalysisId = null) => {
    OverlayHelper.zoneIndex = zoneIndex;
    console.log('=== Starting intertidal zones calculation ===');
    console.log('Manual threshold:', manualThresholdValue);
    console.log('Editing analysis?', editAnalysisId || '(new)');

    const datesToUse = availableDates || dates;

    if (!datesToUse || datesToUse.length === 0) {
      console.error('ERROR: No dates available to calculate intertidal zones!');
      return { dataBar: [], threshold: 0, analysisId: null };
    }

    const geometry = geometryRef.current;
    const bounds = boundsRef.current;
    const images = imagesRef.current;

    const satellite = getSelectedSatellite(true);
    const firstImageName = datesToUse[0]?.name;
    const type = window.sessionStorage.getItem('typeSatellite');
    const missions = type === 'TOA' ? satellite.missions : satellite.surfaceReflectance;
    console.log(missions, 'AAAAAAA', firstImageName);
    let mission = missions.find((m) => m.name === firstImageName);

    if (!mission) mission = missionRef.current || satellite.missions[0];

    console.log('Mission for calculation:', mission?.name, 'firstImageName:', firstImageName);
    console.log('misions', mission, 'name:', mission?.name, 'bands:', getRgbBands(mission));
    let dataBarLocal = [];
    let thresholdJSNumLocal = 0;

    console.log('4. dates', datesToUse);

    console.log('Creating image collection from images:', images);
    let imageCollection = ee.ImageCollection.fromImages(images);

    const bands = getMissionBands(mission);
    const resolution = getMissionResolution(mission);
    /**
     * @function mask
     * @description Applies satellite-aware cloud/QA masking to an image, tagging it with its
     * cloud ratio (0 for Landsat, since gap-filling already handled it upstream).
     * @param {ee.Image} image - Image to mask.
     * **Called by:
     * @see {calculateIntertidalZones} - via the image collection's map.
     * @memberof module:INTERTIDAL
     * @returns {ee.Image} The masked image, tagged with a `CLOUDS` property.
     */
    function mask(image) {
      if (isSentinelMission(mission)) {
        let imgMasked = maskS2ImageMask(image, mission);
        let ratio = maskS2Clouds(imgMasked, geometry, bands.qa, resolution);
        return imgMasked.set('CLOUDS', ratio);
      } else if (isLandsatMission(mission)) {
        return image.set('CLOUDS', ee.Number(0));
      }
      return image;
    }

    var Bay_Plenty_collection = imageCollection;

    if (isLandsatMission(mission)) {
      console.log('Applying gap filling for Landsat collection');
      Bay_Plenty_collection = Bay_Plenty_collection.map(fillGaps);
    }

    Bay_Plenty_collection = Bay_Plenty_collection.map(mask);

    /**
     * @function reflec_corr
     * @description Applies satellite-aware reflectance scaling to convert raw band values into
     * surface reflectance.
     * @param {ee.Image} image - Image to correct.
     * **Called by:
     * @see {calculateIntertidalZones} - via the image collection's map.
     * @memberof module:INTERTIDAL
     * @returns {ee.Image} The image with its optical bands rescaled to reflectance.
     */
    function reflec_corr(image) {
      if (isSentinelMission(mission)) {
        var opticalBands = image.select('B.*').multiply(0.0001);
        return image.addBands(opticalBands, null, true);
      } else if (isLandsatMission(mission)) {
        var opticalBands = image.select('SR_B.*').multiply(0.0000275).add(-0.2);
        return image.addBands(opticalBands, null, true);
      }
      return image;
    }

    Bay_Plenty_collection = Bay_Plenty_collection.map(reflec_corr);

    var vis_rgb = {
      max: 0.25,
      bands: getRgbBands(mission),
    };

    var singleBandVis = {
      min: -0.5,
      max: 1,
    };


    /**
     * @function indexFunction
     * @description Derives the satellite-aware NDWI, MNDWI (regular and sharpened), and AWEI
     * bands for an image and adds them to it.
     * @param {ee.Image} image - Reflectance-corrected image.
     * @see {@link getBandMappingForSatellite}
     * **Called by:
     * @see {calculateIntertidalZones} - via the image collection's map, twice.
     * @memberof module:INTERTIDAL
     * @returns {ee.Image} The image with ndwi/mndwi_sharp/mndwi/awei bands added.
     */
    var indexFunction = function (image) {
      var ndwi, mndwi_sharp, mndwi, awei;

      if (isSentinelMission(mission)) {
        ndwi = image.normalizedDifference(['B3', 'B8']).rename('ndwi');
        mndwi_sharp = image.normalizedDifference(['B3', 'B11_sharp']).rename('mndwi_sharp');
        mndwi = image.normalizedDifference(['B3', 'B11']).rename('mndwi');

        awei = image
          .expression('(B + (2.5*G) - 1.5*(N+S1) - (0.25*S2))', {
            B: image.select('B2'),
            G: image.select('B3'),
            N: image.select('B8'),
            S1: image.select('B11_sharp'),
            S2: image.select('B12_sharp'),
          })
          .rename('awei');
      } else if (isLandsatMission(mission)) {
        var bands = getBandMappingForSatellite(image);

        var B = image.select(ee.String(bands.get('BLUE')));
        var G = image.select(ee.String(bands.get('GREEN')));
        var N = image.select(ee.String(bands.get('NIR')));
        var S1 = image.select(ee.String(bands.get('SWIR1')));
        var S2 = image.select(ee.String(bands.get('SWIR2')));

        ndwi = G.subtract(N).divide(G.add(N)).rename('ndwi');

        mndwi = G.subtract(S1).divide(G.add(S1)).rename('mndwi');
        mndwi_sharp = mndwi.rename('mndwi_sharp');

        awei = image
          .expression('(B + 2.5 * G - 1.5 * (N + S1) - 0.25 * S2)', {
            B: B,
            G: G,
            N: N,
            S1: S1,
            S2: S2,
          })
          .rename('awei');
      }

      return image.addBands([ndwi, mndwi_sharp, mndwi, awei]);
    };

    /**
     * @function clip_image
     * @description Clips an image to the area of interest geometry.
     * @param {ee.Image} image - Image to clip.
     * **Called by:
     * @see {calculateIntertidalZones} - via NWI's map.
     * @memberof module:INTERTIDAL
     * @returns {ee.Image} The clipped image.
     */
    var clip_image = (image) => image.clip(geometry);

    /**
     * @function mask_land
     * @description Masks out land pixels, keeping only mndwi_sharp values >= -0.2 (suited for
     * macrotidal environments).
     * @param {ee.Image} image - Image with an `mndwi_sharp` band.
     * **Called by:
     * @see {calculateIntertidalZones} - via NWINoMask's map.
     * @memberof module:INTERTIDAL
     * @returns {ee.Image} The image with land pixels masked out.
     */
    var mask_land = function (image) {
      var ndwi = image.select('mndwi_sharp');
      return image.updateMask(ndwi.gte(-0.2));
    };

    var NWI = Bay_Plenty_collection.map(indexFunction);

    var NWINoMask = NWI.map(clip_image);

    let params = JSON.parse(window.localStorage.getItem('thumbnailParams')) || {};
    params.region = geometry;
    params.bands = getRgbBands(mission);

    var NWI = NWINoMask.map(mask_land);
    let thumbnailURL = NWI.first().getThumbURL(params);
    console.log('8. thumbnailURL - NWI', 'debug', thumbnailURL);
    var aweiVis = {
      max: 0.2,
      min: -0.2,
    };

    var NWI_STD = NWI.select(zoneIndex).reduce(ee.Reducer.stdDev());
    NWI_STD = NWI_STD.toFloat();

    // Renamed back to the fixed 'awei_stdDev' label regardless of which index was actually
    // selected above, so every downstream reference (histogram keys, dict wrapping, etc.) keeps
    // working unchanged no matter which of the 4 indices the user picked.
    NWI_STD = NWI_STD.select(`${zoneIndex}_stdDev`).rename('awei_stdDev');

    var vis_awei = {
      dimensions: 512,
      format: 'jpg',
      max: 0.25,
      bands: ['awei_stdDev'],
      region: geometry,
    };
    thumbnailURL = NWI_STD.getThumbURL(vis_awei);

    var arrays = NWI_STD.reduceRegion({
      reducer: ee.Reducer.toList(),
      geometry: geometry,
      scale: 30,
      bestEffort: true,
    });


    var list = ee.List(arrays.get('awei_stdDev'));

    var minValue = list.reduce(ee.Reducer.min());
    var maxValue = list.reduce(ee.Reducer.max());
    let minValueLocal = minValue.getInfo();
    let maxValueLocal = maxValue.getInfo();
    console.log('14. minValue:', minValueLocal, 'maxValue:', maxValueLocal);

    if (minValueLocal !== null && maxValueLocal !== null && minValueLocal < maxValueLocal) {
      var histogram = list.reduce(ee.Reducer.fixedHistogram(minValue, maxValue, 200));
      histogram = ee.List(histogram);

      var counts = ee.Array(histogram).slice(1, 1, 2);
      var flatCounts = counts.toList().map(function (item) {
        return ee.List(item).get(0);
      });
      counts = flatCounts;

      var Means = ee.Array(histogram).slice(1, 0, 2);

      var bucketMeans = Means.toList().map(function (item) {
        return ee.List(item).get(0);
      });

      var bucketWidth = ee.Number(ee.List(counts).get(1)).subtract(ee.List(counts).get(0));

      var result = ee.Dictionary({
        bucketMeans: bucketMeans,
        bucketMin: minValue,
        bucketWidth: bucketWidth,
        histogram: counts,
      });


      histogram = ee.Dictionary({ awei_stdDev: result });


      var AWEI_stdDev = histogram.get('awei_stdDev');


      var histogramOld = NWI_STD.reduceRegion({
        reducer: ee.Reducer.histogram(),
        geometry: geometry,
        scale: 10,
      });

      var histogramData = histogramOld.getInfo();
      console.log('histogramData', histogramData);

      var AWEI_stdDev = histogram.get('awei_stdDev');
      var x = histogramData.awei_stdDev.bucketMeans.map(function (el) {
        return el;
      });
      var y = histogramData.awei_stdDev.histogram.map(function (el) {
        return el;
      });

      for (var i = 0; i < x.length; i++) {
        dataBarLocal.push([x[i], y[i]]);
      }

      /**
       * @function otsu
       * @description Computes Otsu's dynamic threshold from the AWEI standard-deviation
       * histogram, maximizing the between-class variance of the two resulting groups.
       * @param {ee.Dictionary} histogram - Unused directly; reads `AWEI_stdDev` from the closure instead.
       * **Called by:
       * @see {calculateIntertidalZones} - when no manual threshold is given.
       * @memberof module:INTERTIDAL
       * @returns {ee.Number} The Otsu threshold value.
       */
      var otsu = function (histogram) {
        var counts = ee.Array(ee.Dictionary(AWEI_stdDev).get('histogram'));
        var means = ee.Array(ee.Dictionary(AWEI_stdDev).get('bucketMeans'));
        var size = means.length().get([0]);
        var total = counts.reduce(ee.Reducer.sum(), [0]).get([0]);
        var sum = means.multiply(counts).reduce(ee.Reducer.sum(), [0]).get([0]);
        var mean = sum.divide(total);

        var indices = ee.List.sequence(1, size);
        var bss = indices.map(function (i) {
          var aCounts = counts.slice(0, 0, i);
          var aCount = aCounts.reduce(ee.Reducer.sum(), [0]).get([0]);
          var aMeans = means.slice(0, 0, i);
          var aMean = aMeans.multiply(aCounts).reduce(ee.Reducer.sum(), [0]).get([0]).divide(aCount);
          var bCount = total.subtract(aCount);
          var bMean = sum.subtract(aCount.multiply(aMean)).divide(bCount);
          return aCount.multiply(aMean.subtract(mean).pow(2)).add(bCount.multiply(bMean.subtract(mean).pow(2)));
        });

        return means.sort(bss).get([-1]);
      };

      var threshold = null;

      if (manualThresholdValue === -1) {
        threshold = otsu(histogram.get('histogram'));
      } else {
        threshold = ee.Number(Number.parseFloat(manualThresholdValue));
      }

      thresholdJSNumLocal = threshold.getInfo();

      var stdMasked = NWI_STD.updateMask(NWI_STD.gte(threshold));
      var zones = NWI_STD.gte(threshold);
      zones = zones.updateMask(zones.neq(0));
      var hist_intermare = stdMasked.reduceRegion({
        reducer: ee.Reducer.histogram(),
        geometry: geometry,
        scale: 10,
      });
      var vectors = zones.addBands(NWI_STD).reduceToVectors({
        crs: 'EPSG:4326',
        scale: 10,
        geometryType: 'polygon',
        labelProperty: 'stdMasked',
        eightConnected: false,
        geometry: geometry,
        maxPixels: 100e9,
        geometryInNativeProjection: true,
        reducer: ee.Reducer.mean(),
      });

      OverlayHelper.vectorsToExport = vectors;

      /**
       * @function clip_image2
       * @description Clips an image to the vectorized intertidal zone.
       * @param {ee.Image} image - Image to clip.
       * **Called by:
       * @see {calculateIntertidalZones} - via NWI2's map.
       * @memberof module:INTERTIDAL
       * @returns {ee.Image} The image clipped to the intertidal zone vectors.
       */
      var clip_image2 = function (image) {
        return image.clip(vectors);
      };

      var NWI2 = Bay_Plenty_collection.map(indexFunction);
      var intertidal_zones = NWI2.map(clip_image2);

      var vis_params = {
        min: -0.5,
        max: 1,
        region: geometry,
        dimensions: 512,
        palette: ['white', 'blue'],
      };

      window.layersCalculated = [];
      OverlayHelper.imgDownloadURL = [];
      OverlayHelper.imgsToStep5 = [];
      OverlayHelper.aweiImages = [];
      var layerURLsToCache = [];
      var layerVisibilityInitial = {};
      var n = intertidal_zones.getInfo().features.length;

      /**
       * The single-layer download link belongs to the last image of the run: it is one string, and
       * the loop below used to overwrite it on every pass. Asking Earth Engine for it inside the
       * loop meant one blocking request per image whose answer was thrown away on the next pass -
       * on a run of thirty images, twenty-nine round trips for a link nobody would ever hold. The
       * last image is remembered instead and asked for once, after the loop.
       */
      let lastMapLayer = null;
      let lastMapLayerName = '';

      var list_unclipped = NWI2.toList(n, 0);
      var list = intertidal_zones.toList(n, 0);

      for (var i = 0; i < n; i++) {
        var img = ee.Image(list.get(i));
        let imgMapLayer = img.select(zoneIndex);
        let url = imgMapLayer.getThumbURL(vis_params);
        let fileNamePrefix =
          typeof datesToUse[i].date === 'string' ? datesToUse[i].date.substring(0, 10) : i.toString();

        lastMapLayer = imgMapLayer;
        lastMapLayerName = fileNamePrefix;

        let overlayIntertidalZone = new window.google.maps.GroundOverlay(url, bounds);
        overlayIntertidalZone.setMap(mapRef.current);
        window.layersCalculated.push(overlayIntertidalZone);
        OverlayHelper.overlayCache.push(overlayIntertidalZone);

        layerURLsToCache.push({
          url: url,
          date: fileNamePrefix,
        });

        OverlayHelper.aweiImages.push({
          image: imgMapLayer,
          date: fileNamePrefix,
        });

        layerVisibilityInitial[fileNamePrefix] = true;

        var img_unclipped = ee.Image(list_unclipped.get(i));
        OverlayHelper.imgsToStep5.push(img_unclipped);

        let imageMission = satellite.missions.find((m) => m.name === datesToUse[i]?.name) || mission;
        let imageBands = getMissionBands(imageMission);
        let imageResolution = getMissionResolution(imageMission);

        let downloadBands;
        if (isSentinelMission(imageMission)) {
          downloadBands = [
            'B2',
            'B3',
            'B4',
            'B8',
            'B11',
            'B11_sharp',
            'B12',
            'B12_sharp',
            'awei',
            'ndwi',
            'mndwi',
            'mndwi_sharp',
          ];
        } else if (isLandsatMission(imageMission)) {
          downloadBands = [
            imageBands.blue,
            imageBands.green,
            imageBands.red,
            imageBands.nir,
            imageBands.swir,
            imageBands.swir2,
            'awei',
            'ndwi',
            'mndwi',
            'mndwi_sharp',
          ].filter((b) => b !== undefined);
        }

        if (downloadBands && downloadBands.length > 0) {
          OverlayHelper.imgDownloadURL.push(
            img_unclipped.getDownloadURL({
              name: fileNamePrefix,
              region: geometry,
              bands: downloadBands,
              scale: imageResolution,
            }),
          );
        }
      }

      if (lastMapLayer) {
        OverlayHelper.urlImagMapLayerDownload = lastMapLayer.getDownloadURL({
          region: geometry,
          name: lastMapLayerName,
          scale: resolution,
        });
      }


      setLayerVisibility(layerVisibilityInitial);

      mapRef.current.setCenter(centerRef.current);

      setDataBar(dataBarLocal);
      setThresholdJSNum(thresholdJSNumLocal);

      const existingAnalysis = editAnalysisId ? analyses.find((a) => a.id === editAnalysisId) : null;
      const analysisId = existingAnalysis ? existingAnalysis.id : `analysis_${analysisCounterRef.current}`;
      if (!existingAnalysis) analysisCounterRef.current += 1;

      const zoneIndexOptionLabel = t(
        `forms.intertidal.${INDEX_OPTIONS.find((option) => option.value === zoneIndex)?.labelKey || 'indexAwei'}`,
      );

      const newAnalysis = {
        id: analysisId,
        name: existingAnalysis ? existingAnalysis.name : `${zoneIndexOptionLabel} ${analysisCounterRef.current - 1}`,
        threshold: thresholdJSNumLocal,
        layers: window.layersCalculated.slice(),
        layerURLs: layerURLsToCache,
        aweiImages: OverlayHelper.aweiImages.slice(),
        dataBar: dataBarLocal,
        visibility: true,
        vectors: OverlayHelper.vectorsToExport,
        downloadURLs: OverlayHelper.imgDownloadURL.slice(),
        imgsToStep5: OverlayHelper.imgsToStep5.slice(),
        urlImagMapLayerDownload: OverlayHelper.urlImagMapLayerDownload,
        zoneIndex,
      };

      if (existingAnalysis) {
        existingAnalysis.layers.forEach((layer) => layer.setMap(null));
      }

      setAnalyses((prev) => {
        if (existingAnalysis) {
          return prev.map((a) => (a.id === analysisId ? newAnalysis : a));
        }

        const updated = [...prev, newAnalysis];

        if (prev.length === 0) {
          setSelectedAnalysisId(analysisId);
          setTimeout(() => {
            OverlayHelper.vectorsToExport = newAnalysis.vectors;
            OverlayHelper.imgDownloadURL = newAnalysis.downloadURLs;
            OverlayHelper.imgsToStep5 = newAnalysis.imgsToStep5;
            OverlayHelper.urlImagMapLayerDownload = newAnalysis.urlImagMapLayerDownload;
          }, 100);
        }

        return updated;
      });

      if (existingAnalysis && selectedAnalysisId === analysisId) {
        OverlayHelper.vectorsToExport = newAnalysis.vectors;
        OverlayHelper.imgDownloadURL = newAnalysis.downloadURLs;
        OverlayHelper.imgsToStep5 = newAnalysis.imgsToStep5;
        OverlayHelper.urlImagMapLayerDownload = newAnalysis.urlImagMapLayerDownload;
        OverlayHelper.aweiImages = newAnalysis.aweiImages;
        OverlayHelper.aweiThreshold = newAnalysis.threshold;
      }

      console.log('=== Calculation completed successfully ===');
      console.log(existingAnalysis ? 'Analysis updated:' : 'New analysis created:', newAnalysis.name, '| ID:', analysisId);
      return {
        dataBar: dataBarLocal,
        threshold: thresholdJSNumLocal,
        analysisId,
      };
    } else {
      console.error('It was not calculated. minValue is', minValueLocal, '- maxValue is', maxValueLocal);
      setDataBar([]);
      setThresholdJSNum(0);
      return { dataBar: [], threshold: 0, analysisId: null };
    }
  };

  // Deliberately does NOT auto-run calculateIntertidalZones anymore: it used to compute a first
  // analysis automatically (always with the default AWEI/NDWI indices) as soon as the map/dates
  // were ready, which the user never asked for and couldn't preview before it happened — they'd
  // rather pick the indices first and press "Criar" themselves. Only the map/AOI/per-date image
  // setup (index-independent) still runs eagerly here, since calculateIntertidalZones needs it.
  useEffect(() => {
    console.log('Checking if initialization can start... mapReady =', mapReady, '| dates length =', dates?.length || 0);
    if (!mapReady || !dates || dates.length === 0) {
      console.log('Waiting: mapReady =', mapReady, '| dates length =', dates?.length || 0);
      return;
    }

    if (initializationDone.current) return;

    window.sessionStorage.removeItem('intertidalLayerURLs');
    window.sessionStorage.removeItem('dataBar');
    window.sessionStorage.removeItem('thresholdIntertidal');
    window.sessionStorage.setItem('toCalculate', '1');

    initializeMapAndGeometry(dates);

    initializationDone.current = true;

    if (centerRef.current) {
      mapRef.current.setCenter(centerRef.current);
    }
  }, [mapReady, dates]);

  useEffect(() => {
    console.log('Checking if AWEI layers need to be added to map... mapReady =', mapReady);
    if (mapReady && window.layersCalculated && window.layersCalculated.length > 0) {
      console.log('Map ready! Ensuring AWEI layers are visible...');
      window.layersCalculated.forEach((layer, index) => {
        if (layer && layer.getMap() === null) {
          console.log(`Adding layer ${index} to map`);
          layer.setMap(mapRef.current);
        }
      });
    }
  }, [mapReady]);

  /**
   * @function getCoordinates
   * @description Extracts [lng, lat] coordinates from a drawn rectangle or polygon overlay. Not
   * currently called anywhere in the codebase.
   * @param {Object} shape - Drawn shape, with `type` ('rectangle'|'polygon') and `overlay`.
   * @memberof module:INTERTIDAL
   * @returns {Array<Array<Number>>} Coordinates as [lng, lat] pairs.
   */
  function getCoordinates(shape) {
    let coordinates = [];
    if (shape.type === 'rectangle') {
      const bounds = shape.overlay.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      coordinates = [
        [ne.lng(), ne.lat()],
        [ne.lng(), sw.lat()],
        [sw.lng(), sw.lat()],
        [sw.lng(), ne.lat()],
        [ne.lng(), ne.lat()],
      ];
    }

    if (shape.type === 'polygon') {
      const path = shape.overlay.getPath();
      const array = path.getArray();
      coordinates = array.map((p) => [p.lng(), p.lat()]);
    }

    return coordinates;
  }

  /**
   * @function handleDrawing
   * @description Hides the map's drawing controls once a region is drawn.
   * @param {google.maps.Polygon|google.maps.Rectangle} overlay - Drawn overlay (unused).
   * @param {Array<Array<Number>>} coordinates - Drawn coordinates (unused).
   * **Called by:
   * @see {IntertidalZoneDefiner} - via GoogleMap's onRegionDrawn prop.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleDrawing = (overlay, coordinates) => {
    MapGoogle.setDrawingControlsVisible(false);
  };

  /**
   * @function handleChoose
   * @description Validates that an analysis has been selected, then syncs `OverlayHelper` to
   * that analysis's cached vectors/images/download URLs before advancing to the next step.
   * Explicitly returns `false` when no analysis is selected — `StepperButtons`' onNext only blocks
   * navigation on a literal `false` return, so without it "Next" would silently advance to step 5
   * anyway, carrying forward whatever stale data `OverlayHelper` last held.
   * @see {@link OverlayHelper}
   * **Called by:
   * @see {IntertidalZoneDefiner} - via StepperButtons' onNext.
   * @memberof module:INTERTIDAL
   * @returns {boolean|void} `false` to block navigation when no analysis is selected.
   */
  const handleChoose = () => {
    if (!selectedAnalysisId) {
      dispatch(Snack.error('intertidal-select-analysis-required', t('forms.intertidal.selectAnalysisRequired')));
      return false;
    }
    const selectedAnalysis = analyses.find((a) => a.id === selectedAnalysisId);
    if (!selectedAnalysis) {
      dispatch(Snack.error('intertidal-select-analysis-required', t('forms.intertidal.selectAnalysisRequired')));
      return false;
    }
    OverlayHelper.vectorsToExport = selectedAnalysis.vectors;
    OverlayHelper.imgDownloadURL = selectedAnalysis.downloadURLs;
    OverlayHelper.imgsToStep5 = selectedAnalysis.imgsToStep5;
    OverlayHelper.urlImagMapLayerDownload = selectedAnalysis.urlImagMapLayerDownload;
    OverlayHelper.aweiLayerURLs = selectedAnalysis.layerURLs;
    OverlayHelper.aweiImages = selectedAnalysis.aweiImages || [];
    OverlayHelper.aweiThreshold = selectedAnalysis.threshold;
    OverlayHelper.zoneIndex = selectedAnalysis.zoneIndex || 'awei';
  };

  const steps = [
    {
      selector: '#areachooser',
      content: t('tour.interdial.2.aoi'),
    },
    {
      selector: '#interfialZone',
      content: t('tour.interdial.2.interdialZone'),
    },
    {
      selector: '#importKml',
      content: t('tour.interdial.2.importKml'),
    },
  ];

  const [isTourOpen, setIsTourOpen] = useLocalStorage('showROITour', true);

  /**
   * @function crosshair
   * @description Registers a Dygraph plugin that draws a crosshair (vertical/horizontal/both)
   * through the currently selected point on the histogram graph, via its `select`/`deselect`
   * lifecycle callbacks.
   * @param {Object} [opt_options] - Plugin options.
   * @param {String} [opt_options.direction] - Crosshair direction: 'vertical', 'horizontal', or 'both'.
   * **Called by:
   * @see {IntertidalZoneDefiner} - registered once as `Dygraph.Plugins.Crosshair`, then instantiated by `showThresholdDialog`'s Dygraph plugin config.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  Dygraph.Plugins.Crosshair = (function () {
    'use strict';
    var crosshair = function (opt_options) {
      this.canvas_ = document.createElement('canvas');
      opt_options = opt_options || {};
      this.direction_ = opt_options.direction || null;
    };

    crosshair.prototype.toString = function () {
      return 'Crosshair Plugin';
    };
    crosshair.prototype.activate = function (g) {
      g.graphDiv.appendChild(this.canvas_);

      return {
        select: this.select,
        deselect: this.deselect,
      };
    };

    crosshair.prototype.select = function (e) {
      if (this.direction_ === null) {
        return;
      }
      var width = e.dygraph.width_;
      var height = e.dygraph.height_;
      this.canvas_.width = width;
      this.canvas_.height = height;
      this.canvas_.style.width = width + 'px';
      this.canvas_.style.height = height + 'px';
      var ctx = this.canvas_.getContext('2d');
      ctx.clearRect(0, 0, width, height);
      ctx.strokeStyle = 'rgba(0, 0, 0,0.3)';
      ctx.beginPath();
      var canvasx = Math.floor(e.dygraph.selPoints_[0].canvasx) + 0.5;
      if (this.direction_ === 'vertical' || this.direction_ === 'both') {
        ctx.moveTo(canvasx, 0);
        ctx.lineTo(canvasx, height);
      }
      if (this.direction_ === 'horizontal' || this.direction_ === 'both') {
        for (var i = 0; i < e.dygraph.selPoints_.length; i++) {
          var canvasy = Math.floor(e.dygraph.selPoints_[i].canvasy) + 0.5;
          ctx.moveTo(0, canvasy);
          ctx.lineTo(width, canvasy);
        }
      }
      ctx.stroke();
      ctx.closePath();
    };

    crosshair.prototype.deselect = function (e) {
      var ctx = this.canvas_.getContext('2d');
      ctx.clearRect(0, 0, this.canvas_.width, this.canvas_.height);
    };

    crosshair.prototype.destroy = function () {
      this.canvas_ = null;
    };

    return crosshair;
  })();

  const dataBarFormatted = dataBar.map((el) => {
    return [Number.parseFloat(el[0].toFixed(4)), el[1]];
  });
  const thresholdFormatted = Number.parseFloat(thresholdJSNum.toFixed(4));
  const labels = dataBarFormatted.map((el) => el[0]);
  const minRange = labels.length > 0 ? Math.min(...labels) : 0;
  const maxRange = labels.length > 0 ? Math.max(...labels) : 1;
  const [rangeValue, setRangeValue] = useState(thresholdFormatted);

  useEffect(() => {
    setRangeValue(Number.parseFloat(thresholdJSNum.toFixed(4)));
  }, [thresholdJSNum]);

  /**
   * @function handleChange
   * @description Legacy class-component-style handler; references `this.setState`, which does
   * not exist in this function component. Not currently called anywhere in the codebase.
   * @param {Event} event - Change event from the threshold range input.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleChange = function (event) {
    this.setState({ rangeValue: event.target.value });
  };

  /**
   * @function showThresholdDialog
   * @description Opens the manual threshold dialog and (after a short delay for the dialog to
   * render) draws the standard-deviation histogram as a Dygraph bar chart with a vertical
   * crosshair, letting the user click a bar to pick a candidate threshold.
   * @param {Array} dataBarOverride - Histogram data to preload (each `[bin, frequency]`) — always
   * passed explicitly by both call sites (the just-created analysis's own result, or an existing
   * analysis's stored `dataBar`) rather than read from `dataBar` state, since React state updates
   * triggered in the same tick that opens this dialog wouldn't be visible yet to the function
   * closure captured at render time.
   * @param {number} thresholdOverride - Threshold to preload the slider/input with, same reasoning.
   * @see {@link barPlotter}
   * @see {@link crosshair}
   * **Called by:
   * @see {handleCreateRasterWithSelectedIndices} - right after creating a new analysis, so the
   * user reviews/adjusts the automatic threshold as part of creating it.
   * @see {handleEditAnalysis} - when the user reopens an existing analysis for adjustment.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const showThresholdDialog = function (dataBarOverride, thresholdOverride) {
    setDataBar(dataBarOverride);
    setThresholdJSNum(thresholdOverride);

    const dataFormatted = (dataBarOverride || []).map((el) => [Number.parseFloat(el[0].toFixed(4)), el[1]]);
    const parsedThreshold = Number.parseFloat(Number(thresholdOverride || 0).toFixed(4));

    let dialog = document.getElementById('change_threshold');
    document.getElementById('threshold').value = thresholdOverride;
    setRangeValue(parsedThreshold);
    dialog.showModal();

    setTimeout(() => {
      const g = new Dygraph(
        document.getElementById('graph'),
        dataFormatted,
        {
          labels: ['Bin', 'Frequency'],
          plotter: barPlotter,

          plugins: [
            new Dygraph.Plugins.Crosshair({
              direction: 'vertical',
            }),
          ],

          pointClickCallback: function (e, pt) {
            setRangeValue(pt.xval);
          },
          color: 'orange',
          strokeWidth: 1,
          drawPoints: true,
          pointSize: 15,
          highlightCircleSize: 3,
        },
        {
          fillGraph: false,
        },
      );
    }, 2000);
  };

  /**
   * @function closeThresholdDialog
   * @description Closes the manual threshold dialog.
   * **Called by:
   * @see {redoThreshold}
   * @see {IntertidalZoneDefiner} - via the threshold dialog's "Cancel" button.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const closeThresholdDialog = function () {
    let dialog = document.getElementById('change_threshold');
    dialog.close();
  };

  /**
   * @function redoThreshold
   * @description Recalculates the intertidal zones using the manual threshold entered by the
   * user, showing a loading dialog while the calculation runs. Applies to whichever analysis
   * `editingAnalysisId` currently points at — the one just created (dialog opened automatically
   * right after "Criar") or an existing one reopened via its "Editar" button — replacing that same
   * entry in place rather than appending a new one.
   * @see {@link calculateIntertidalZones}
   * @see {@link closeThresholdDialog}
   * **Called by:
   * @see {IntertidalZoneDefiner} - via the threshold dialog's "Confirm" button.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const redoThreshold = function () {
    const threshold = document.getElementById('threshold').value;
    const targetAnalysisId = editingAnalysisId;
    closeThresholdDialog();
    setLoadingLabel(t('forms.core.loading.zones'));
    setIsLoading(true);

    setTimeout(() => {
      calculateIntertidalZones(threshold, dates, targetAnalysisId);

      setManualThreshold(threshold);
      setEditingAnalysisId(null);

      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }, 100);
  };

  /**
   * @function handleCreateRasterWithSelectedIndices
   * @description Commits the two currently-picked (but not yet applied) index selectors and
   * creates a brand-new analysis from them — mirrors `redoThreshold`'s loading-dialog pattern.
   * Deliberately does NOT run as the selectors' own onChange: picking a new index only stages it
   * locally (`zoneIndex`/`waterlineIndex` state) so the user can line up both choices first: this
   * is the one place that actually spends an Earth Engine computation and adds a new entry to the
   * analyses list, so it only fires on an explicit "Create" click, never on every dropdown change.
   * Always computes with the automatic (Otsu) threshold, same as before — the dialog is not opened
   * automatically; if the user wants to adjust the threshold afterward, they use the "Editar" icon
   * on that analysis in the list below.
   * @see {@link handleEditAnalysis}
   * **Called by:
   * @see {IntertidalZoneDefiner} - via the "Create" button next to the two index selectors.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleCreateRasterWithSelectedIndices = () => {
    OverlayHelper.waterlineIndex = waterlineIndex;
    setLoadingLabel(t('forms.core.loading.zones'));
    setIsLoading(true);

    setTimeout(() => {
      calculateIntertidalZones(-1, dates);
      setManualThreshold(-1);

      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }, 100);
  };

  /**
   * @function handleEditAnalysis
   * @description Reopens the threshold dialog pre-filled with an existing analysis's own
   * histogram/threshold, and restores its zone index too (so the recalculation stays consistent
   * with whichever index that analysis originally used, regardless of what's currently picked in
   * the selectors above). Confirming recalculates and replaces that same analysis in place.
   * @param {String} analysisId - ID of the analysis to edit.
   * @see {@link showThresholdDialog}
   * @see {@link redoThreshold}
   * **Called by:
   * @see {IntertidalZoneDefiner} - via each analysis list item's "Editar" icon.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleEditAnalysis = (analysisId) => {
    const analysis = analyses.find((a) => a.id === analysisId);
    if (!analysis) return;
    setZoneIndex(analysis.zoneIndex || 'awei');
    setEditingAnalysisId(analysisId);
    showThresholdDialog(analysis.dataBar || [], analysis.threshold || 0);
  };

  /**
   * @function barPlotter
   * @description Custom Dygraph plotter that renders each data point as a solid bar (histogram
   * style) instead of a line.
   * @param {Object} e - Dygraph plotter event, with drawingContext, points, dygraph, and color.
   * **Called by:
   * @see {showThresholdDialog} - via the Dygraph `plotter` option.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  function barPlotter(e) {
    var ctx = e.drawingContext;
    var points = e.points;
    var y_bottom = e.dygraph.toDomYCoord(0);
    ctx.fillStyle = e.color;
    var barWidth = 0.5 * (points[1].canvasx - points[0].canvasx);
    for (var i = 0; i < points.length; i++) {
      var p = points[i];
      var center_x = p.canvasx;
      ctx.fillRect(center_x - barWidth / 2, p.canvasy, barWidth, y_bottom - p.canvasy);
      ctx.strokeRect(center_x - barWidth / 2, p.canvasy, barWidth, y_bottom - p.canvasy);
    }
  }

  /**
   * @function toggleLayerVisibility
   * @description Toggles a single AWEI layer's visibility on the map, by date. Legacy path kept
   * for compatibility; not currently called anywhere in the codebase (superseded by
   * toggleAnalysisVisibility).
   * @param {String} layerDate - Date key of the layer to toggle.
   * @param {Number} index - Index of the layer within `window.layersCalculated`.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const toggleLayerVisibility = (layerDate, index) => {
    if (window.layersCalculated && window.layersCalculated[index]) {
      const isVisible = !layerVisibility[layerDate];
      window.layersCalculated[index].setMap(isVisible ? mapRef.current : null);
      setLayerVisibility((prev) => ({ ...prev, [layerDate]: isVisible }));
    }
  };

  /**
   * @function toggleAnalysisVisibility
   * @description Toggles the map visibility of every layer belonging to a given analysis.
   * @param {String} analysisId - ID of the analysis to toggle.
   * **Called by:
   * @see {IntertidalZoneDefiner} - via each analysis list item's visibility icon.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const toggleAnalysisVisibility = (analysisId) => {
    setAnalyses((prev) =>
      prev.map((analysis) => {
        if (analysis.id === analysisId) {
          const newVisibility = !analysis.visibility;
          analysis.layers.forEach((layer) => {
            layer.setMap(newVisibility ? mapRef.current : null);
          });
          return { ...analysis, visibility: newVisibility };
        }
        return analysis;
      }),
    );
  };

  /**
   * @function deleteAnalysis
   * @description Removes an analysis's layers from the map and from state. If the deleted one was
   * selected, re-syncs `OverlayHelper` to the first remaining analysis (via
   * `selectAnalysisForNextStep`) — or, if none remain, resets every `OverlayHelper` field back to
   * its empty default. Without this, `OverlayHelper` kept holding the deleted analysis's cached
   * vectors/images/URLs (it's only ever written to, never cleared, by `calculateIntertidalZones`/
   * `selectAnalysisForNextStep`/`handleChoose`), so deleting the only analysis and pressing "Next"
   * anyway silently carried that already-deleted raster forward into step 5 and the result page.
   * @param {String} analysisId - ID of the analysis to delete.
   * @see {@link selectAnalysisForNextStep}
   * @see {@link OverlayHelper}
   * **Called by:
   * @see {IntertidalZoneDefiner} - via each analysis list item's delete icon.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const deleteAnalysis = (analysisId) => {
    const analysis = analyses.find((a) => a.id === analysisId);
    if (!analysis) return;

    analysis.layers.forEach((layer) => {
      layer.setMap(null);
    });

    const remaining = analyses.filter((a) => a.id !== analysisId);
    setAnalyses(remaining);

    if (selectedAnalysisId === analysisId) {
      if (remaining.length > 0) {
        selectAnalysisForNextStep(remaining[0].id);
      } else {
        setSelectedAnalysisId(null);
        OverlayHelper.vectorsToExport = null;
        OverlayHelper.imgDownloadURL = [];
        OverlayHelper.imgsToStep5 = [];
        OverlayHelper.urlImagMapLayerDownload = '';
        OverlayHelper.aweiLayerURLs = [];
        OverlayHelper.aweiImages = [];
        OverlayHelper.aweiThreshold = 0;
      }
    }
  };

  /**
   * @function selectAnalysisForNextStep
   * @description Selects an analysis to carry forward to the next step, syncing `OverlayHelper`
   * to its cached vectors/images/download URLs.
   * @param {String} analysisId - ID of the analysis to select.
   * @see {@link OverlayHelper}
   * **Called by:
   * @see {IntertidalZoneDefiner} - via each analysis list item's onClick.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const selectAnalysisForNextStep = (analysisId) => {
    setSelectedAnalysisId(analysisId);

    const analysis = analyses.find((a) => a.id === analysisId);
    if (analysis) {
      OverlayHelper.vectorsToExport = analysis.vectors;
      OverlayHelper.imgDownloadURL = analysis.downloadURLs;
      OverlayHelper.imgsToStep5 = analysis.imgsToStep5;
      OverlayHelper.urlImagMapLayerDownload = analysis.urlImagMapLayerDownload;
      OverlayHelper.aweiLayerURLs = analysis.layerURLs;
      OverlayHelper.aweiImages = analysis.aweiImages || [];
      OverlayHelper.aweiThreshold = analysis.threshold;
      OverlayHelper.zoneIndex = analysis.zoneIndex || 'awei';
    }
  };

  /**
   * @function GenerateZip
   * @description Downloads each URL as a (single-image) zip, re-zips all of their contents into
   * one combined zip file (one folder per image), and triggers its download.
   * @param {Array<String>} urlsArray - Array of per-image zip download URLs.
   * **Called by:
   * @see {downloadImages}
   * @memberof module:INTERTIDAL
   * @returns {Promise<void>}
   */
  const GenerateZip = async (urlsArray) => {
    const finalZip = new JSZip();
    for (let i = 0; i < urlsArray.length; i++) {
      const url = urlsArray[i];
      const response = await fetch(url);
      const blob = await response.blob();
      const zip = await JSZip.loadAsync(blob);
      const folder = finalZip.folder(`image_${i + 1}`);
      const filePromises = [];
      zip.forEach((relativePath, file) => {
        filePromises.push(
          file.async('blob').then((content) => {
            folder.file(relativePath, content);
          }),
        );
      });
      await Promise.all(filePromises);
    }
    const content = await finalZip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(content);
    link.download = 'images.zip';
    link.click();
  };

  /**
   * @function downloadImages
   * @description Zips and downloads the currently cached per-image download URLs, showing a
   * loading dialog while it runs.
   * @see {@link GenerateZip}
   * @see {@link OverlayHelper}
   * **Called by:
   * @see {IntertidalZoneDefiner} - via the "Download images" button's onClick.
   * @memberof module:INTERTIDAL
   * @returns {Promise<void>}
   */
  const downloadImages = async () => {
    setLoadingLabel(t('forms.core.loading.export'));
    setIsLoading(true);
    try {
      await GenerateZip(OverlayHelper.imgDownloadURL);
    } catch (error) {
      console.error('Error generating download URLs: ', error);
    }
    setIsLoading(false);
  };

  const zoneIndexLabel = t(
    `forms.intertidal.${INDEX_OPTIONS.find((option) => option.value === zoneIndex)?.labelKey || 'indexAwei'}`,
  );

  return (
    <React.Fragment>
      {isLoading && <ActivityIndicator forceShow={true} title={loadingLabel || t('forms.core.loading.zones')} />}
      <dialog id="change_threshold" style={{ width: '80%', height: 630 }}>
        <div>
          <div>
            <Box textAlign="center" mb={2}>
              <Typography variant="h6" component="h2">
                {t('forms.intertidal.4.histogramTitle', { index: zoneIndexLabel })}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('forms.intertidal.4.histogramDescription')}
              </Typography>
            </Box>
            <div id="graph" style={{ width: '100%', height: 320 }}></div>
            <br />
            <div style={{ width: '100%', textAlign: 'right' }}>
              <div style={{ width: '94%', display: 'inline-block' }}>
                <Slider
                  key={1}
                  value={rangeValue}
                  step={0.0005}
                  min={minRange}
                  max={maxRange}
                  onChange={(_, value) => {
                    setRangeValue(value);
                  }}
                />
              </div>
            </div>
            <div style={{ width: '100%' }}>
              <div style={{ float: 'left' }}>{minRange}</div>
              <div style={{ float: 'right' }}>{maxRange}</div>
            </div>
            <p align="center">{rangeValue}</p>
          </div>
        </div>
        <input type="text" id="threshold" value={rangeValue} readOnly style={{ display: 'none' }} />
        <br />
        <p align="center">
          <Button
            onClick={() => {
              closeThresholdDialog();
              setEditingAnalysisId(null);
            }}
            color="secondary"
            variant="contained"
            style={{
              cursor: 'pointer',
              padding: '30',
              margin: 10,
            }}>
            CANCEL
          </Button>
          &nbsp;
          <Button
            onClick={redoThreshold}
            color="primary"
            variant="contained"
            style={{
              cursor: 'pointer',
              padding: '30',
              margin: 10,
            }}>
            {t('forms.intertidal.next')}
          </Button>
        </p>
      </dialog>

      <Box>
        <HelpButton
          onClickFunction={() => {
            setIsTourOpen(true);
          }}
        />
        <GoogleMap style={{ width: '100%', height: 500 }} onRegionDrawn={handleDrawing} />

        <br />

        <div style={{ textAlign: 'center' }}>
          <br />
          <Box mb={3} display="flex" justifyContent="center">
            <Paper elevation={2} style={{ padding: '16px', maxWidth: '700px', width: '100%' }}>
              <Typography variant="h6" gutterBottom>
                {t('forms.intertidal.chooseIndexTitle')}
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" justifyContent="center">
                <TextField
                  select
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 200 }}
                  label={t('forms.intertidal.zoneIndexLabel')}
                  value={zoneIndex}
                  onChange={(e) => setZoneIndex(e.target.value)}>
                  {SELECTABLE_INDEX_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(`forms.intertidal.${option.labelKey}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 200 }}
                  label={t('forms.intertidal.waterlineIndexLabel')}
                  value={waterlineIndex}
                  onChange={(e) => setWaterlineIndex(e.target.value)}>
                  {SELECTABLE_INDEX_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(`forms.intertidal.${option.labelKey}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{ height: 40 }}
                  onClick={handleCreateRasterWithSelectedIndices}>
                  {t('forms.intertidal.createRaster')}
                </Button>
              </Box>
            </Paper>
          </Box>
          {analyses && analyses.length > 0 && (
            <Box mb={3} display="flex" justifyContent="center">
              <Paper elevation={2} style={{ padding: '16px', maxWidth: '700px', width: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  {t('forms.intertidal.analysesTitle', { index: zoneIndexLabel }) || 'Analyses on Map'}
                </Typography>
                <List dense>
                  {analyses.map((analysis, index) => (
                    <ListItem
                      key={analysis.id}
                      style={{
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: selectedAnalysisId === analysis.id ? '#e3f2fd' : 'transparent',
                      }}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <span
                              style={{
                                fontWeight: selectedAnalysisId === analysis.id ? 'bold' : 'normal',
                              }}>
                              {analysis.name}
                            </span>
                            {selectedAnalysisId === analysis.id && (
                              <span
                                style={{
                                  fontSize: '0.75rem',
                                  backgroundColor: '#4caf50',
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                }}>
                                {t('forms.intertidal.selected')}
                              </span>
                            )}
                          </Box>
                        }
                        secondary={
                          <span>
                            {t('forms.intertidal.threshold')}: {analysis.threshold.toFixed(4)} |{' '}
                            {analysis.layers.length} {t('forms.intertidal.aweiLayers', { index: zoneIndexLabel })}
                          </span>
                        }
                      />

                      <IconButton
                        edge="end"
                        onClick={() => toggleAnalysisVisibility(analysis.id)}
                        color={analysis.visibility ? 'primary' : 'default'}
                        title={
                          analysis.visibility ? t('forms.intertidal.hideAnalysis') : t('forms.intertidal.showAnalysis')
                        }>
                        {analysis.visibility ? <Visibility /> : <VisibilityOff />}
                      </IconButton>

                      <Button
                        size="small"
                        variant={selectedAnalysisId === analysis.id ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => selectAnalysisForNextStep(analysis.id)}
                        style={{ marginLeft: '8px', minWidth: '80px' }}>
                        {selectedAnalysisId === analysis.id
                          ? t('forms.intertidal.usingAnalysis')
                          : t('forms.intertidal.useAnalysis')}
                      </Button>

                      <IconButton
                        edge="end"
                        onClick={() => handleEditAnalysis(analysis.id)}
                        title={t('forms.intertidal.editAnalysis')}
                        style={{ marginLeft: '8px' }}>
                        <Edit />
                      </IconButton>

                      <IconButton
                        edge="end"
                        onClick={() => {
                          if (window.confirm(t('forms.intertidal.deleteAnalysisConfirm', { name: analysis.name }))) {
                            deleteAnalysis(analysis.id);
                          }
                        }}
                        title="Delete analysis"
                        style={{ marginLeft: '8px' }}>
                        <Delete style={{ color: '#f44336' }} />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}
          <Button
            color="primary"
            variant="outlined"
            style={{ display: 'inline' }}
            disabled={!selectedAnalysisId}
            onClick={() => {
              if (!selectedAnalysisId) {
                dispatch(Snack.error('intertidal-select-analysis-required', t('forms.intertidal.selectAnalysisRequired')));
                return;
              }
              const selectedAnalysis = analyses.find((a) => a.id === selectedAnalysisId);
              if (!selectedAnalysis) return;

              Export.table.toDevice.asShapefileGroup(
                [
                  {
                    features: selectedAnalysis.vectors.getInfo().features.map((intertidalArea) => ({
                      ...intertidalArea,
                      properties: omit(intertidalArea.properties, [INTERNALS]),
                    })),
                  },
                ],
                Export.defaultOptions.device.shapefileGroup('output', 'vectors'),
              );
            }}>
            {t('forms.intertidal.downloadVectors')}
          </Button>
          &nbsp;&nbsp;
          <Button
            color="primary"
            variant="outlined"
            style={{ display: 'inline' }}
            disabled={!selectedAnalysisId}
            onClick={() => {
              if (!selectedAnalysisId) {
                dispatch(Snack.error('intertidal-select-analysis-required', t('forms.intertidal.selectAnalysisRequired')));
                return;
              }
              const selectedAnalysis = analyses.find((a) => a.id === selectedAnalysisId);
              if (!selectedAnalysis) return;
              downloadImages();
            }}>
            {t('forms.intertidal.downloadImages')}
          </Button>
          &nbsp;&nbsp;
          <br />
        </div>

        <StepperButtons navigate={navigate} onNext={handleChoose} nextDisabled={!selectedAnalysisId} />

        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      </Box>
    </React.Fragment>
  );
};

export default IntertidalZoneDefiner;
